import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCompass,
  FiGlobe,
  FiMail,
  FiMap,
  FiMapPin,
  FiPhone,
} from "react-icons/fi";
import { FaFacebookF, FaInstagram } from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";

import CommerceAdaptiveImage from "./CommerceAdaptiveImage";
import CommerceContactLink from "./CommerceContactLink";
import CommerceMapPanel from "./CommerceMapPanel";

import { cn } from "../utils/commercesUtils";

export default function CommerceCard({
  commerce,
  isMapOpen = false,
  onToggleMap,
  delay = 0,
}) {
  return (
    <Reveal delay={delay}>
      <article className="group flex h-full flex-col overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-[#1277B9]/50 hover:shadow-xl dark:border-slate-800 dark:bg-slate-950">
        <div className="relative">
          <CommerceAdaptiveImage
            src={commerce.image}
            alt={commerce.title}
            className="h-64"
          />

          {commerce.logo && (
            <div className="absolute left-4 top-4 z-30 h-14 w-14 overflow-hidden rounded-2xl border border-white/80 bg-white shadow-lg">
              <img
                src={commerce.logo}
                alt={`Logo ${commerce.title}`}
                className="h-full w-full object-contain p-1"
              />
            </div>
          )}

          <div className="absolute bottom-4 left-4 right-4 z-30 flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              {commerce.category && (
                <span className="inline-flex rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-[#1277B9] shadow-sm backdrop-blur dark:bg-slate-950/90 dark:text-sky-300">
                  {commerce.category}
                </span>
              )}

              <h2 className="mt-2 line-clamp-2 text-2xl font-bold leading-tight text-white drop-shadow-sm">
                {commerce.title}
              </h2>
            </div>

            {commerce.distanceLabel && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-950/65 px-3 py-1.5 text-xs font-bold text-white shadow-sm backdrop-blur">
                <FiCompass className="h-3.5 w-3.5" />
                {commerce.distanceLabel}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-1 flex-col p-5">
          {commerce.descriptionPreview && (
            <p className="line-clamp-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
              {commerce.descriptionPreview}
            </p>
          )}

          {commerce.address && (
            <div className="mt-4 flex items-start gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
              <FiMapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#1277B9]" />
              {commerce.mapsHref ? (
                <a
                  href={commerce.mapsHref}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#1277B9] hover:underline"
                >
                  {commerce.address}
                </a>
              ) : (
                <span>{commerce.address}</span>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <CommerceContactLink
              href={commerce.phoneHref}
              icon={FiPhone}
              label={commerce.phone || "Téléphoner"}
            />

            <CommerceContactLink
              href={commerce.mail ? `mailto:${commerce.mail}` : ""}
              icon={FiMail}
              label="Email"
              variant={commerce.mail ? "primary" : "default"}
            />

            <CommerceContactLink
              href={commerce.websiteUrl}
              icon={FiGlobe}
              label="Site"
              external
            />

            <CommerceContactLink
              href={commerce.facebookUrl}
              icon={FaFacebookF}
              label="Facebook"
              external
            />

            <CommerceContactLink
              href={commerce.instagramUrl}
              icon={FaInstagram}
              label="Instagram"
              external
            />
          </div>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <Link
              to={`/commerces/${commerce.slug}`}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-[#1277B9] px-4 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#0f659d] focus:outline-none focus:ring-2 focus:ring-[#1277B9]/40 sm:flex-none"
            >
              En savoir plus
              <FiArrowRight className="h-4 w-4" />
            </Link>

            {commerce.hasMap && (
              <button
                type="button"
                onClick={onToggleMap}
                className={cn(
                  "inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border px-4 py-2 text-sm font-bold transition sm:flex-none",
                  isMapOpen
                    ? "border-[#1277B9] bg-[#1277B9] text-white shadow-sm hover:bg-[#0f659d]"
                    : "border-slate-200 bg-white text-slate-700 hover:border-[#1277B9]/50 hover:bg-sky-50 hover:text-[#1277B9] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                )}
              >
                <FiMap className="h-4 w-4" />
                {isMapOpen ? "Masquer la carte" : "Voir la carte"}
              </button>
            )}
          </div>

          {isMapOpen && <CommerceMapPanel commerce={commerce} />}
        </div>
      </article>
    </Reveal>
  );
}